import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  codeWords,
  saveFile,
  vendorQueryFromHash,
  pctValue,
  tierLabel,
  type CompiledFilter,
  type IntentCompiled,
  type Vendor,
  type VendorPage,
  type VendorQuery,
} from '../lib/api'
import { statusWord } from '../lib/status'
import { ownerContact, phoneWords, usePeopleContacts } from '../lib/people'
import { BandChip } from '../components/BandChip'
import { CoverageCell, DataTable, ErrorBox, Loading, type Column } from '../components/DataTable'
import { IntentBar } from '../components/IntentBar'
import { FilterBar, type FilterGroup } from '../components/FilterBar'
import '../styles/registers.css'

// The filter sends the stored ordinal, which is what the API's tier filter
// takes and what sorts by exposure. What is printed is tierLabel: the ordinal
// runs the other way round from the name, so a chip reading Tier 4 would select
// the vendors a reader thinks it excludes.
const BANDS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'INSUFFICIENT_EVIDENCE']
const STATUSES = ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'OFFBOARDING', 'OFFBOARDED', 'REJECTED']
const CATEGORIES = ['PERSONAL', 'SENSITIVE_PERSONAL', 'FINANCIAL', 'HEALTH']

// Sorting by band puts the rows that need a decision at one end. Insufficient
// evidence ranks above HIGH rather than below LOW: an unknown band is not a
// good one, and burying it under the low-risk rows is how it stops being read.
const BAND_RANK: Record<string, number> = {
  CRITICAL: 5,
  INSUFFICIENT_EVIDENCE: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
}

// One control, one vocabulary. Every screen that lists vendors offers the
// same groups in the same order, so a filter learned here is the same filter
// on the register.
const FILTERS: FilterGroup[] = [
  { key: 'tier', label: 'Tier', options: ['4', '3', '2', '1'].map((t) => ({ value: t, label: tierLabel(Number(t)) })) },
  { key: 'band', label: 'Risk band', options: BANDS.map((b) => ({ value: b, label: label(b) })) },
  // The vendor's stage in the vocabulary's words (lib/status.ts).
  { key: 'status', label: 'Stage', options: STATUSES.map((s) => ({ value: s, label: statusWord('vendor', s).word })) },
  { key: 'dataCategory', label: 'Data handled', options: CATEGORIES.map((c) => ({ value: c, label: label(c) })) },
  {
    key: 'flag',
    label: 'Also',
    options: [
      { value: 'crossBorder', label: 'Data moves outside India' },
      { value: 'overdue', label: 'Overdue only' },
    ],
  },
]

function label(v: string): string {
  return v.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

// The filter a compiled phrase stands for, as the query the list and the
// export both read. The phrase compiles to the same filter the chips send, so
// exporting a compiled result exports exactly its rows.
function queryOfCompiled(f: CompiledFilter): VendorQuery & { dueBefore?: string } {
  return {
    tier: (f.Tiers ?? []).map(String),
    band: f.Bands ?? [],
    status: f.Statuses ?? [],
    dataCategory: f.DataCategories ?? [],
    overdue: f.OverdueOnly,
    crossBorder: f.CrossBorder === true,
    q: f.Search || undefined,
    owner: f.Owner || undefined,
    dueBefore: f.DueBefore ? f.DueBefore.slice(0, 10) : undefined,
  }
}

export function Portfolio() {
  // Opened from a card on the overview, the register starts filtered by the
  // thing that card counted. Read once, at mount, rather than watched: after
  // that the chips on this screen are what the filter is, and a hash that kept
  // reasserting itself would fight them.
  const [filter, setFilter] = useState<VendorQuery>(() => vendorQueryFromHash(window.location.hash))
  // The header bar's search narrows this list; this screen keeps no box.
  useEffect(() => {
    const onQuery = (e: Event) => {
      const q = (e as CustomEvent<string>).detail ?? ''
      setFilter((f) => (f.q === q || (!f.q && !q) ? f : { ...f, q }))
    }
    window.addEventListener('tprm:query', onQuery)
    return () => window.removeEventListener('tprm:query', onQuery)
  }, [])
  const [page, setPage] = useState<VendorPage | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [intent, setIntent] = useState<{ phrase: string; result: IntentCompiled } | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<ApiError | null>(null)
  const people = usePeopleContacts()

  const reload = useCallback(() => {
    let live = true
    setLoading(true)
    api
      .vendors(filter)
      .then((p) => {
        if (!live) return
        setPage(p)
        setError(null)
      })
      .catch((err) => {
        if (!live) return
        setPage(null)
        setError(err)
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [filter])

  useEffect(() => {
    // A compiled intent owns the result set until it is cleared, so a refetch
    // triggered by the filter chips also drops it.
    setIntent(null)
    return reload()
  }, [reload])

  const shown = intent ? intent.result : page
  const vendors: Vendor[] = shown?.vendors ?? []
  const owner = (v: Vendor) => ownerContact(people, v.ownerUserId, v.businessOwnerUserId)

  // The rows in view, as a workbook: the chips' filter, or the compiled
  // phrase's where one owns the list.
  function exportToExcel(): void {
    setExporting(true)
    setExportError(null)
    api
      .portfolioExport(intent ? queryOfCompiled(intent.result.filter) : filter)
      .then(saveFile)
      .catch((err) => setExportError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setExporting(false))
  }

  const columns: Column<Vendor>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      sortValue: (v) => v.displayName || v.legalName,
      render: (v) => (
        <>
          <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(v.id)}>
            {v.displayName || v.legalName}
          </a>
          <span className="cell-sub">
            {[v.vendorCode, codeWords(v.constitution), v.stateCode, v.msmeCategory ? codeWords(v.msmeCategory) + ' MSME' : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </>
      ),
    },
    {
      key: 'tier',
      // Sorted on the ordinal and printed as the label. Sorting by name would
      // order the register alphabetically, which puts critical between high and
      // low; sorting by the ordinal keeps the column ordered by exposure.
      header: 'Tier',
      sortValue: (v) => v.inherentTier ?? null,
      render: (v) => (v.inherentTier ? tierLabel(v.inherentTier) : <span className="muted">-</span>),
    },
    {
      key: 'band',
      header: 'Risk',
      sortValue: (v) => (v.currentBand ? BAND_RANK[v.currentBand] ?? 0 : null),
      render: (v) => <BandChip band={v.currentBand} />,
    },
    {
      key: 'coverage',
      header: 'Coverage',
      numeric: true,
      sortValue: (v) => pctValue(v.coveragePct),
      render: (v) => <CoverageCell value={pctValue(v.coveragePct)} />,
    },
    {
      key: 'attested',
      header: 'Attested',
      numeric: true,
      sortValue: (v) => pctValue(v.attestationPct),
      render: (v) => {
        const a = pctValue(v.attestationPct)
        return a === null ? <span className="muted">-</span> : <span className="mono">{Math.round(a)}%</span>
      },
    },
    {
      // The accountable person, the owner and else the business owner, as a
      // name, an address and the work phone kept in People and roles (review
      // 3.4). Never the stored identifier.
      key: 'owner',
      header: 'Owner',
      sortValue: (v) => {
        const o = owner(v)
        return o.state === 'UNASSIGNED' ? null : (o.name || o.email).toLowerCase()
      },
      render: (v) => {
        const o = owner(v)
        if (o.state !== 'PERSON') return <span className="muted">{o.name}</span>
        return (
          <>
            <span className="cell-primary reg-owner">{o.name || o.email}</span>
            {o.name && o.email && (
              <a className="cell-sub reg-owner" href={'mailto:' + o.email}>
                {o.email}
              </a>
            )}
            {o.phone && (
              <a className="cell-sub reg-owner" href={'tel:' + o.phone}>
                {phoneWords(o.phone)}
              </a>
            )}
          </>
        )
      },
    },
  ]

  return (
    <>
      <div className="page-head">
        <h1>Portfolio</h1>
        <button type="button" data-action="tprm.portfolio.a02" onClick={exportToExcel} disabled={exporting || !shown}>
          {exporting ? 'Exporting' : 'Export to Excel'}
        </button>
      </div>

      {exportError && (
        <div className="error-box" role="alert">
          <h3>The export did not download ({exportError.code})</h3>
          <p>{exportError.message}</p>
        </div>
      )}

      <FilterBar
        groups={FILTERS}
        value={{
          tier: filter.tier ?? [],
          band: filter.band ?? [],
          status: filter.status ?? [],
          dataCategory: filter.dataCategory ?? [],
          flag: [filter.crossBorder ? 'crossBorder' : '', filter.overdue ? 'overdue' : ''].filter(Boolean),
        }}
        onChange={(v) =>
          setFilter((f) => ({
            ...f,
            tier: v.tier ?? [],
            band: v.band ?? [],
            status: v.status ?? [],
            dataCategory: v.dataCategory ?? [],
            crossBorder: (v.flag ?? []).includes('crossBorder'),
            overdue: (v.flag ?? []).includes('overdue'),
          }))
        }
        panelExtra={
          <IntentBar
            onCompiled={(result, phrase) => setIntent({ result, phrase })}
            onCleared={() => {
              setIntent(null)
              reload()
            }}
          />
        }
      />

      <section className="panel panel-flush" aria-labelledby="results-heading">
        <div className="panel-head">
          <h2 id="results-heading" style={{ margin: 0 }}>
            {intent ? 'Compiled result' : 'Portfolio'}
          </h2>
          {intent && <span className="mono summary-line">{intent.phrase}</span>}
        </div>

        {loading && !shown && <Loading what="the vendor portfolio" />}
        {error != null && <ErrorBox error={error} what="The vendor portfolio" />}

        {shown && (
          <DataTable
            caption="Vendors in this tenant, with inherent tier, current risk, evidence coverage, attestation ratio and owner."
            columns={columns}
            rows={vendors}
            rowKey={(v) => v.id}
            defaultSort={{ key: 'band', dir: 'desc' }}
            empty={{
              title: shown.totalInScope > 0 ? 'Nothing matches these filters' : 'No vendors yet',
              detail:
                shown.totalInScope > 0
                  ? shown.totalInScope + ' vendors in scope, clear the filters to see them'
                  : 'The register is empty',
              action:
                shown.totalInScope > 0
                  ? undefined
                  : { label: 'Import vendors', href: '#/import' },
            }}
          />
        )}

        {/* Matched against total in scope, always, under the list the count
            describes. A filtered answer that reports only its own row count
            hides the omission. The count is withheld when the fetch failed,
            because "0 of 0" and "we never got an answer" are different
            statements. */}
        {shown && (
          <p className="summary-line reg-foot" role="status">
            <strong>{shown.matched}</strong> of <strong>{shown.totalInScope}</strong> vendors
          </p>
        )}
      </section>
    </>
  )
}
